import { useRef, useState } from 'react';
import { Box, TextField, Tooltip } from '@mui/material';
import { Message } from 'common/types';
import AudioRecorder from '../AudioRecorder';
import useSendMessageContainerLogic from './useSendMessageContainerLogic';
import SendMedia from '../SendMedia';
import { useSelectedDeviceStore } from 'store/useSelectedDeviceStore';
import { useMixpanel } from 'utils/hooks/useMixPanel';
import { Check, Close as CancelIcon, Send as SendIcon } from '@mui/icons-material';

import { AudioOrSendButton } from 'components';

import {
  ScheduleMessageModal,
  SchedulePickerModal,
} from './components';
import { defaultColorPallete } from 'utils';
import { canModifyScheduledMessage } from 'utils/canModifyScheduledMessage';

type Props = {
  currentMessages: Message[];
  editingScheduledMessage?: {id: string, newSendAt: string | Date, message: string};
  setEditingScheduledMessage?: (editingScheduledMessage: {id: string, newSendAt: string | Date, message: string}) => void;
  onEditScheduledMessageEnd?: (editingScheduledMessage: {id: string, newSendAt: string | Date, message: string}) => void;
  sendScheduledMessage?: (editingScheduledMessage: {id: string, message: string}) => void;
  onScheduleMessage?: (editingScheduledMessage: {sendAt: string | Date, message: string}) => void;
};

export default function SendMessageContainer(props: Props) {
  const {
    currentMessages,
    editingScheduledMessage,
    setEditingScheduledMessage,
    onEditScheduledMessageEnd,
    sendScheduledMessage,
    onScheduleMessage
  } = props;

  const {
    selectedConversation,
    requestSendMediaMessage,
    requestSendTextMessage,
  } = useSendMessageContainerLogic({ currentMessages });

  const [showAudioRecorder, setShowAudioRecorder] = useState<boolean>(false);
  const [showSchedulePicker, setShowSchedulePicker] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");

  const resetMessageInput = () => {
    setMessage("");
  };

  const inputRef = useRef<HTMLInputElement | null>(null);

  const openKeyboard = () => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.click();
    }
  };

  const { chatInteraction, contentSent } = useMixpanel();
  const { selectedDevice } = useSelectedDeviceStore();

  const onSendTextMessage = () => {
    if (selectedDevice && selectedConversation?.id) {
      // Dispara o evento imediatamente no clique do usuário
      chatInteraction(selectedDevice, "message-sent", {
        conversationId: selectedConversation?.id,
      });
      contentSent(selectedDevice, "text");
    }
    requestSendTextMessage(message, resetMessageInput, openKeyboard);
  };

  return (
    <>
      <ScheduleMessageModal
        isOpen={showModal}
        setIsModalOpen={setShowModal}
        setIsScheduleModalOpen={setShowSchedulePicker}
        message={message}
        onSendMessage={(props) => {
          setShowModal(false);
          setMessage('');
          onScheduleMessage(props);
        }}
      />
      <SchedulePickerModal
        isOpen={showSchedulePicker}
        setIsModalOpen={setShowSchedulePicker}
        onScheduleMessage={(props) => {
          setShowSchedulePicker(false);
          onScheduleMessage({ sendAt: props.sendAt, message });
        }}
      />
      <Box
        key={`SendMessageContainer-${selectedConversation?.id}`}
        minHeight="80px"
        display="flex"
        width="100%"
        bgcolor="#F5F5F5"
        justifyContent="center"
        alignItems="center"
      >
        <Box
          width={{ xs: "100%", md: "90%" }}
          display="flex"
          alignItems={showAudioRecorder ? "flex-end" : "center"}
          flexDirection="column"
          p={1}
        >
          {showAudioRecorder && (
            <AudioRecorder
              onCloseAudioRecorder={(show) => setShowAudioRecorder(show)}
              requestSendAudioMessage={requestSendMediaMessage}
              selectedConversation={selectedConversation}
              selectedDevice={selectedDevice}
            />
          )}
          {!showAudioRecorder && (
            <Box
              width="100%"
              display="flex"
              alignContent="center"
              alignItems="center"
            >
              <SendMedia
                requestSendMediaMessage={requestSendMediaMessage}
                selectedConversation={selectedConversation}
                selectedDevice={selectedDevice}
              />
              {editingScheduledMessage ? (
                <TextField
                  inputRef={inputRef}
                  autoFocus={true}
                  fullWidth
                  multiline
                  maxRows={8}
                  placeholder="Escreva sua mensagem..."
                  onChange={(e) => setEditingScheduledMessage({
                    id: editingScheduledMessage.id,
                    newSendAt: editingScheduledMessage.newSendAt,
                    message: e.target.value
                  })}
                  value={editingScheduledMessage?.message}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (message) {
                        onSendTextMessage();
                      }
                    }
                  }}
                />
              ) : (
                <TextField
                  inputRef={inputRef}
                  autoFocus={true}
                  fullWidth
                  multiline
                  maxRows={8}
                  placeholder="Escreva sua mensagem..."
                  onChange={(e) => setMessage(e.target.value)}
                  value={message}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (message) {
                        onSendTextMessage();
                      }
                    }
                  }}
              />
              )}
              {editingScheduledMessage ? (
                <AudioOrSendButton
                  tooltip='Salvar alterações'
                  icon={<Check sx={{ color: 'white' }} />}
                  text={editingScheduledMessage.message}
                  onSend={() => onEditScheduledMessageEnd(editingScheduledMessage)}
                  dropdownOptions={[
                    {
                      icon: <CancelIcon sx={{ color: defaultColorPallete.text.primary }} />,
                      label: 'Cancelar ajuste',
                      onClick: () => setEditingScheduledMessage(undefined),
                    },
                    {
                      icon: <SendIcon sx={{ color: defaultColorPallete.text.primary }} />,
                      label: 'Enviar agora',
                      disabled: !canModifyScheduledMessage(editingScheduledMessage.newSendAt).success,
                      onClick: () => sendScheduledMessage({
                        id: editingScheduledMessage.id,
                        message: editingScheduledMessage.message
                      }),
                    },
                  ]}
                />
              ) : (
                <AudioOrSendButton
                  text={message}
                  onSend={onSendTextMessage}
                  onMic={() => setShowAudioRecorder(true)}
                  onArrowDown={() => setShowModal(true)}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}
